/**
 * Developer Tokens Page — token-kit.com
 *
 * Lets developers claim their own test token so they can test LLM integration
 * end-to-end without needing a real ai-tokens.me user account.
 *
 * Mirrors the ai-tokens.me purchase flow but styled with the token-kit.com
 * emerald design system and includes the raw token value for SDK use.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  Wallet,
  Zap,
  ArrowRight,
} from 'lucide-react';
import {
  getDeveloperTokenBalance,
  getDeveloperTokenPackages,
  claimDeveloperTokens,
} from '../services';
import type { TokenPackage, DeveloperTokenBalance, ClaimResult } from '../services';

const ENABLED_PACKAGES = new Set(['pkg_free']);

export default function DeveloperTokensPage() {
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [balance, setBalance] = useState<DeveloperTokenBalance | null>(null);
  const [selected, setSelected] = useState<string>('pkg_free');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<ClaimResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [pkgs, bal] = await Promise.all([
        getDeveloperTokenPackages(),
        getDeveloperTokenBalance(),
      ]);
      setPackages(pkgs);
      setBalance(bal);
    } catch {
      setError('Failed to load token information. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleClaim = async () => {
    if (!selected || claiming) return;
    setClaiming(true);
    setError(null);
    try {
      const res = await claimDeveloperTokens(selected);
      setResult(res);
      // Refresh balance after successful claim
      const updated = await getDeveloperTokenBalance();
      setBalance(updated);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Claim failed. Please try again.';
      setError(msg);
    } finally {
      setClaiming(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
    }
  };

  if (loading) return <div className="page-loader">Loading developer tokens…</div>;

  // ── Success Screen ─────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="dev-tokens-page">
        <div className="page-header">
          <div>
            <h1>Developer User Tokens</h1>
            <p>Your personal LLM test tokens</p>
          </div>
        </div>

        <div className="dev-tokens-success">
          <CheckCircle2 size={64} className="success-icon" />
          <h2>Tokens Claimed!</h2>
          <p className="success-subtitle">
            {result.tokensAdded.toLocaleString()} tokens added to your developer account.
          </p>

          <div className="dev-tokens-token-card highlight">
            <div className="token-card-header">
              <Wallet size={16} />
              <span>Your Developer User Token</span>
            </div>
            <p className="token-card-hint">
              Use this token as the <code>userToken</code> in your SDK calls during development.
            </p>
            <div className="token-value-row">
              <code className="token-value">{result.developerToken}</code>
              <div className="copy-btn-wrap">
                {copied && <span className="copy-tooltip">Copied!</span>}
                <button
                  className="copy-btn"
                  onClick={() => handleCopy(result.developerToken)}
                  title="Copy token"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <div className="token-balance-row">
              <span className="balance-label">New balance</span>
              <span className="balance-value">{result.newBalance.toLocaleString()} tokens</span>
            </div>
          </div>

          <div className="dev-tokens-code-hint">
            <p className="code-hint-label">Quick start</p>
            <pre className="code-hint">
              {`import TokenKit from '@codetunezstudios/token-kit';\n\nconst tk = new TokenKit(process.env.TOKENKIT_API_KEY!);\n\nconst reply = await tk.chat(\n  '${result.developerToken.substring(0, 18)}…',\n  [TokenKit.user('Hello!')]\n);`}
            </pre>
          </div>

          <div className="success-actions">
            <button className="btn btn-ghost" onClick={() => setResult(null)}>
              Get More Tokens
            </button>
            <Link to="/dashboard" className="btn btn-accent">
              Go to Dashboard <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedPkg = packages.find((p) => p.id === selected);

  // ── Main Page ──────────────────────────────────────────────────────────────
  return (
    <div className="dev-tokens-page">
      <div className="page-header">
        <div>
          <h1>Developer User Tokens</h1>
          <p>Get your own test tokens to try LLM calls before your users sign up</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* ── Current Balance Card ─────────────────────────────── */}
      {balance && (balance.hasToken || balance.available > 0) && (
        <div className="dev-tokens-token-card">
          <div className="token-card-header">
            <Wallet size={16} />
            <span>Your User Token</span>
            <span className="balance-pill">{balance.available.toLocaleString()} tokens</span>
          </div>
          <p className="token-card-hint">
            Pass this as the <code>userToken</code> in your SDK calls during development.
          </p>
          <div className="token-value-row">
            <code className="token-value">{balance.developerToken}</code>
            <div className="copy-btn-wrap">
              {copied && <span className="copy-tooltip">Copied!</span>}
              <button
                className="copy-btn"
                onClick={() => handleCopy(balance.developerToken)}
                title="Copy token"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Two-Column Layout ────────────────────────────────── */}
      <div className="dev-tokens-layout">

        {/* Left — Package Selection */}
        <div className="dev-tokens-packages">
          <h2>Select a Package</h2>
          <div className="packages-grid">
            {packages.map((pkg) => {
              const enabled = ENABLED_PACKAGES.has(pkg.id);
              const isSelected = selected === pkg.id;
              return (
                <div
                  key={pkg.id}
                  className={`package-card ${isSelected ? 'selected' : ''} ${!enabled ? 'disabled' : ''}`}
                  onClick={() => enabled && setSelected(pkg.id)}
                  role="radio"
                  aria-checked={isSelected}
                  aria-disabled={!enabled}
                  tabIndex={enabled ? 0 : -1}
                  onKeyDown={(e) => e.key === 'Enter' && enabled && setSelected(pkg.id)}
                >
                  {pkg.popular && <div className="popular-badge">Popular</div>}
                  {!enabled && <div className="coming-soon-overlay">Coming Soon</div>}

                  <div className="package-header">
                    <span className="package-name">{pkg.name}</span>
                    {isSelected && <CheckCircle2 size={18} className="selected-check" />}
                  </div>

                  <div className="package-tokens">
                    <Zap size={14} />
                    {pkg.tokens.toLocaleString()} tokens
                  </div>

                  <div className="package-price">
                    {pkg.priceUsd === 0 ? (
                      <span className="price-free">Free</span>
                    ) : (
                      <>
                        <span className="price-amount">${pkg.priceUsd.toFixed(2)}</span>
                        {pkg.savings > 0 && (
                          <span className="price-savings">Save {pkg.savings}%</span>
                        )}
                      </>
                    )}
                  </div>

                  {pkg.priceUsd > 0 && (
                    <div className="package-per-token">
                      ${(pkg.costPerToken * 1000).toFixed(4)} per 1K tokens
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — Claim Panel */}
        <div className="dev-tokens-claim-panel">
          <h2>Claim Tokens</h2>

          <div className="claim-card">
            <div className="payment-methods">
              <p className="payment-label">Payment method</p>
              <div className="payment-options disabled-group">
                <button className="payment-option" disabled>💳 Credit Card</button>
                <button className="payment-option" disabled>PayPal</button>
              </div>
              <p className="payment-coming-soon">
                Paid packages require payment integration — coming soon.
              </p>
            </div>

            <div className="order-summary">
              <p className="summary-label">Order summary</p>
              {selectedPkg && (
                <>
                  <div className="summary-row">
                    <span>{selectedPkg.name} Package</span>
                    <span>{selectedPkg.tokens.toLocaleString()} tokens</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>
                      {selectedPkg.priceUsd === 0
                        ? 'Free'
                        : `$${selectedPkg.priceUsd.toFixed(2)}`}
                    </span>
                  </div>
                </>
              )}
            </div>

            <button
              className="btn btn-accent btn-block"
              onClick={handleClaim}
              disabled={claiming || !selected || !ENABLED_PACKAGES.has(selected)}
            >
              {claiming
                ? 'Claiming…'
                : selectedPkg?.priceUsd === 0
                ? 'Claim Free Tokens'
                : `Select ${selectedPkg?.name} — $${selectedPkg?.priceUsd.toFixed(2)}`}
            </button>

            {!ENABLED_PACKAGES.has(selected) && (
              <p className="claim-unavailable">
                This package will be available once payment integration is set up.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
